import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  AssignmentOutlined as FormIcon,
  VisibilityOutlined as ViewIcon,
  Search,
} from "@mui/icons-material";
import { useAppContext } from "../../context/AppContext";
import moment from "moment";
import { GetPastAppointmentsByUserId } from "../../helpers/AppointmentSchedule";
import { downloadConsentFormByChatId } from "../../helpers/Downloads";
import Toast from "../../components/toast";
import { minutesToTime } from "../../constants/timeUtils";

const PastAppointments = () => {
  const navigate = useNavigate();
  const { isMobile } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 5;

  const GetAllAppointmenthanlder = async () => {
    setIsLoading(true);

    const response = await GetPastAppointmentsByUserId();
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      setAppointments(data ?? []);
    } else {
      Toast.error(response.message);
    }

    // setAppointments([
    //   {
    //     _id: "1",
    //     chatID: "abc",
    //     appointmentDate: "2026-02-28T06:57:21.000Z",
    //     status: "PME & POLST",
    //   },
    //   {
    //     _id: "2",
    //     chatID: "abc",
    //     appointmentDate: "2026-02-20T06:57:21.000Z",
    //     status: "completed",
    //   },
    //   {
    //     _id: "3",
    //     chatID: "abc",
    //     appointmentDate: "2026-01-15T06:57:21.000Z",
    //     status: "completed",
    //   },
    //   {
    //     _id: "4",
    //     chatID: "abc",
    //     appointmentDate: "2026-01-10T06:57:21.000Z",
    //     status: "PME & POLST",
    //   },
    //   {
    //     _id: "5",
    //     chatID: "abc",
    //     appointmentDate: "2026-01-05T06:57:21.000Z",
    //     status: "completed",
    //   },
    //   {
    //     _id: "6",
    //     chatID: "abc",
    //     appointmentDate: "2025-12-25T06:57:21.000Z",
    //     status: "completed",
    //   },
    // ]);

    setIsLoading(false);
  };

  useEffect(() => {
    GetAllAppointmenthanlder();
  }, []);

  // ✅ FILTER
  const filteredAppointments = useMemo(() => {
    return appointments.filter((item) => {
      const formattedDate = moment(item.appointmentDate)
        .format("DD MMM YYYY, h:mm A")
        .toLowerCase();

      return (
        formattedDate.includes(search.toLowerCase()) ||
        item.medicalEvaluation.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [appointments, search]);

  // ✅ PAGINATION
  const paginatedAppointments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAppointments.slice(start, start + rowsPerPage);
  }, [filteredAppointments, page]);

  const pageCount = Math.ceil(filteredAppointments.length / rowsPerPage);

  const handleViewClick = (row) => {
    navigate("/past-appointment-detail", {
      state: { appointmentId: row._id },
    });
  };

  const handleViewConsentForm = async (row) => {
    downloadConsentFormByChatId(row.chatId);
  };

  const styles = {
    tableCell: {
      color: "#6B6B6B",
      fontSize: "14px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    },
  };

  return (
    <Box>
      <Box
        sx={{
          width: "100%",
          border: "1px solid #E8E8E8",
          borderRadius: "12px",
          p: 2,
          backgroundColor: "#fff",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            my: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 2,
          }}
        >
          <Typography fontSize="24px" className="sf-medium sf-medium--dark">
            Past Appointments
          </Typography>

          {/* ✅ SEARCH */}
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 260,
              background: "#fff",
              borderRadius: "8px",
            }}
          />
        </Box>

        {/* TABLE */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              border: "1px solid #E8E8E8",
              borderRadius: "12px",
              overflow: "hidden",
              minWidth: 800,
            }}
          >
            <TableHead sx={{ backgroundColor: "#F5F5FF" }}>
              <TableRow>
                <TableCell sx={{ ...styles.tableCell, width: "25%" }}>
                  Appointment Date & Time
                </TableCell>
                <TableCell sx={styles.tableCell}>Patient Name</TableCell>
                <TableCell sx={styles.tableCell}>Consent Form</TableCell>
                <TableCell sx={styles.tableCell}>Medical Evaluation</TableCell>
                <TableCell sx={styles.tableCell}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedAppointments.length > 0 ? (
                paginatedAppointments.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {moment.utc(row.appointmentDate).format("DD MMM YYYY") +
                        ", " +
                        minutesToTime(row.startMinute) +
                        " - " +
                        minutesToTime(row.endMinute)}
                    </TableCell>
                    <TableCell>{row.patientName}</TableCell>
                    <TableCell>
                      <Button
                        sx={{
                          border: "1px solid #E3E3E5",
                          borderRadius: "6px",
                          textTransform: "none",
                          color: "#1E1E1EBF",
                          minWidth: "130px",
                        }}
                        onClick={() => handleViewConsentForm(row)}
                      >
                        <FormIcon sx={{ mr: 0.5, fontSize: 18 }} />
                        Consent Form
                      </Button>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={
                          {
                            // color: "#7B7DE5",
                            // textDecoration: "underline",
                            // cursor: "pointer",
                          }
                        }
                      >
                        {row.medicalEvaluation}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Button
                        sx={{
                          border: "1px solid #E3E3E5",
                          borderRadius: "6px",
                          textTransform: "none",
                          color: "#1E1E1EBF",
                          minWidth: "80px",
                        }}
                        onClick={() => handleViewClick(row)}
                      >
                        <ViewIcon sx={{ mr: 0.5, fontSize: 18 }} />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    {isLoading ? "Loading..." : "No appointments found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ✅ PAGINATION */}
        {pageCount > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PastAppointments;
